import { useEffect, useState } from 'react';
import { coworkerApi } from '@/lib/api/coworker';
import { useAuthStore, useCoworkerStore } from '@/store';
import useMemoryStore from '@/store/memory';
import useWorkStarterProject from './useWorkStarterProject';
import { projectWorkSuggestions } from '@/constants/projectWorkSuggestions';

export const WORK_STARTER_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
// Keep yesterday's pills available while today's set loads or the app is offline.
const STALE_TTL_MS = 2 * WORK_STARTER_CACHE_TTL_MS;
const MEMORY_REFRESH_DEBOUNCE_MS = 1000;
const cache = new Map();
const pending = new Map();
const listeners = new Map();

function cacheKey(userId, sessionGeneration, collectionId, project) {
  return JSON.stringify(['v10', userId, sessionGeneration, collectionId, project]);
}

function readCache(key, revision, allowStale = false) {
  const hit = cache.get(key);
  if (!hit) return undefined;
  const age = Date.now() - hit.at;
  if (age >= STALE_TTL_MS) {
    cache.delete(key);
    return undefined;
  }
  if (!allowStale && (hit.revision !== revision || age >= WORK_STARTER_CACHE_TTL_MS)) return undefined;
  return hit.items;
}

function initialState(enabled, key, revision) {
  if (!enabled) return { items: null, isPending: false, key };
  const cached = readCache(key, revision, true);
  if (cached !== undefined) return { items: cached, isPending: false, key };
  return { items: null, isPending: true, key };
}

/**
 * Load project-specific pills when a folder is selected, otherwise memory pills.
 * Share preloaded suggestions across screens. Expired entries stay visible
 * while revalidating, including when the composer has been unmounted.
 * Cold starts can render project/default suggestions immediately.
 */
export default function useWorkStarterPrompts({
  collectionId,
  enabled = true,
  refreshKey = 0,
} = {}) {
  const token = useAuthStore((s) => s.token);
  const userId = useAuthStore((s) => s.user?.id);
  const signingOut = useAuthStore((s) => s._isSigningOut);
  const sessionGeneration = useAuthStore((s) => s._sessionGeneration);
  const memoryEnabled = useAuthStore((s) => s.user?.preferences?.memory?.isEnabled !== false);
  const includeMemory = useCoworkerStore((s) => s.composerIncludeMemory);
  const memoryRevision = useMemoryStore((s) => s.workStarterPromptsRevision);
  const project = useWorkStarterProject(collectionId);
  const revision = project ? 0 : memoryRevision;
  const key = cacheKey(userId, sessionGeneration, collectionId, project);
  const [state, setState] = useState(() => initialState(enabled, key, revision));

  const canLoad = Boolean(enabled && token && userId && !signingOut);
  const canPersonalize = Boolean(canLoad && project !== undefined && (project || (memoryEnabled && includeMemory)));

  useEffect(() => {
    if (!canPersonalize) return undefined;
    const subscribers = listeners.get(key) || new Set();
    const update = (items) => setState({ items, isPending: false, key });
    subscribers.add(update);
    listeners.set(key, subscribers);
    return () => {
      subscribers.delete(update);
      if (!subscribers.size) listeners.delete(key);
    };
  }, [canPersonalize, key]);

  useEffect(() => {
    if (!canPersonalize) {
      setState({ items: null, isPending: false, key });
      return undefined;
    }

    const cached = readCache(key, revision);
    if (cached !== undefined) {
      setState({ items: cached, isPending: false, key });
      return undefined;
    }

    let cancelled = false;
    const stale = readCache(key, revision, true);
    setState((prev) => {
      const items = stale !== undefined ? stale : prev.key === key ? prev.items : null;
      return {
        items,
        isPending: stale === undefined && !Array.isArray(items),
        key,
      };
    });
    const isCurrentSession = () => {
      const auth = useAuthStore.getState();
      return !!auth.token && !auth._isSigningOut && auth.user?.id === userId
        && auth._sessionGeneration === sessionGeneration;
    };
    const canCacheResponse = () => isCurrentSession()
      && (project || useMemoryStore.getState().workStarterPromptsRevision === revision);
    const isCurrent = () => !cancelled && canCacheResponse();
    const load = () => {
      if (!isCurrent()) return;
      // Share requests only within the same account, project/collection and revision.
      const requestKey = `${key}:${revision}`;
      let request = pending.get(requestKey);
      if (!request) {
        request = coworkerApi.fetchStarterPrompts({ collectionId, ...(project ? { project } : {}) }).then((data) => {
          const next = data?.personalized && Array.isArray(data.suggestions) && data.suggestions.length
            ? data.suggestions
            : null;
          // A response started before a dream update must not restore stale cache entries.
          if (canCacheResponse()) {
            if (cache.size >= 200 && !cache.has(key)) cache.delete(cache.keys().next().value);
            cache.set(key, { at: Date.now(), items: next, revision });
            listeners.get(key)?.forEach((update) => update(next));
          }
          return next;
        }).finally(() => pending.delete(requestKey));
        pending.set(requestKey, request);
      }
      request.then((next) => {
        if (isCurrent()) setState({ items: next, isPending: false, key });
      }).catch(() => {
        // An offline/failed refresh must not erase the last usable suggestions.
        if (isCurrent()) setState((prev) => ({
          items: readCache(key, revision, true) ?? (prev.key === key ? prev.items : null),
          isPending: false,
          key,
        }));
      });
    };

    // Dream commits arrive as a burst of memory and profile events after persistence.
    const changedAt = project ? 0 : useMemoryStore.getState().workStarterPromptsChangedAt;
    const delay = Math.max(0, changedAt + MEMORY_REFRESH_DEBOUNCE_MS - Date.now());
    const timer = delay > 0 ? setTimeout(load, delay) : null;
    if (!delay) load();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [canPersonalize, collectionId, key, revision, project, userId, sessionGeneration, refreshKey]);

  if (!canPersonalize) return { items: null, isPending: canLoad && project === undefined };
  // A folder switch must never paint the previous project's or account's pills.
  const current = state.key === key ? state : initialState(true, key, revision);
  if (project) return { items: current.items?.length >= 3 ? current.items : projectWorkSuggestions(project),
    isPending: current.isPending, source: 'project' };
  return { items: current.items, isPending: current.isPending };
}
